import React, { useCallback, useMemo, useRef, useState } from 'react';
import Table from '../../components/Table';
import { useTranslation } from 'react-i18next';
import usePaginatedItems from '../hooks/usePaginatedItems';
import api from 'api';
import {
  InventoryLocationsSearchBy,
  locationSearchByOptions,
  SortDirection,
} from 'types';
import useQueryParams from 'hooks/useQueryParams';
import { InventoryType } from 'api/inventoryLocations';
import Modal, { ModalRef } from 'components/Modal';
import InventoryLocationsForm from './components/InventoryLocationsForm';
import { InventoryLocations } from 'models/Inventory';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import utils from 'utils';
import confirm from 'modules/confirm';
import { OptionValue } from 'ncoded-component-library/build/components/molecules/Select/Select.component';
import { useLocation, useParams } from 'react-router-dom';

const InventoryLocationsPage = () => {
  const [binNo, setBinNo] = useState<number>();
  const [isCopy, setIsCopy] = useState(false);
  const [sortOrder, setSortOrder] = useState<Record<string, SortDirection>>({
    binNo: undefined,
    locationName: undefined,
  });

  const location = useLocation();

  const { storeNo } = useParams();

  const stateSkuNo = location.state?.skuNo;

  const { t } = useTranslation();

  const modalRef = useRef<ModalRef>();

  const queryClient = useQueryClient();

  const {
    setQueryParam,
    params: { page, search, limit, sortBy, searchBy, sortDir },
  } = useQueryParams<{
    page: string;
    search: string;
    limit: string;
    searchBy: InventoryLocationsSearchBy;
    sortBy: string;
    sortDir: SortDirection;
  }>({ searchBy: 'locationStatus' });

  const { items, isFetching, onRefresh, totalItems, totalPages } =
    usePaginatedItems({
      queryKey: 'locations',
      makeRequest: api.inventoryLocations.getInventoryLocations,
      searchParam: searchBy,
      params: {
        limit,
        page,
        storeNo,
        skuNo: String(stateSkuNo) || undefined,
        [searchBy]: !!search && search?.length >= 2 ? search : undefined,
      },
      additionalParams: { sortBy, sortDir },
    });

  const searchByOptions = useMemo(
    () =>
      Object.values(locationSearchByOptions).map(({ value, label }) => ({
        label: t(`${label}`),
        value,
      })),
    [t],
  );

  const handleSortClick = useCallback(
    (key: string) => {
      const newDirection = sortOrder[key] === 'ASC' ? 'DESC' : 'ASC';

      setSortOrder((prev) => {
        const updatedSortOrder = Object.keys(prev).reduce(
          (acc, colKey) => ({
            ...acc,
            [colKey]: colKey === key ? newDirection : undefined,
          }),
          {},
        );
        return updatedSortOrder;
      });

      setQueryParam('sortBy', key);
      setQueryParam('sortDir', newDirection);
    },
    [setQueryParam, sortOrder],
  );

  const handleSearchByChange = useCallback(
    (option: OptionValue<any>) => {
      setQueryParam('searchBy', option.value, true);
    },
    [setQueryParam],
  );

  const labels = useMemo(
    () => [
      {
        label: t('InventoryLocations.binNo'),
        key: 'binNo',
        isSortable: true,
        sortDir: sortOrder['binNo'],
      },
      {
        label: t('InventoryLocations.locationName'),
        key: 'locationName',
        isSortable: true,
        sortDir: sortOrder['locationName'],
      },
      {
        label: t('Units'),
        key: 'units',
      },
      {
        label: t('InventoryLocations.sellingStatus'),
        key: 'locationSellingStatus',
      },
      { label: t('InventoryLocations.locationStatus'), key: 'locationStatus' },
      { label: t('InventoryLocations.type'), key: 'type' },
    ],
    [sortOrder, t],
  );
  const renderContentArray = useCallback(
    (data: InventoryType[]) => {
      return data?.map(
        ({ locationName, typeId, binNo, locationSellingStatus, ...rest }) => ({
          id: binNo,
          binNo,
          locationName,
          type: typeId,
          units: 'Single',
          locationSellingStatus:
            locationSellingStatus === 'Available' ? t('YES') : t('NO'),
          ...rest,
        }),
      );
    },
    [t],
  );

  const addLocations = useCallback(
    async (values: InventoryLocations) => {
      try {
        await api.inventoryLocations.addInventoryLocation(values);
        toast.success(t('InventoryLocations.successAddingLocation'));
        modalRef.current.close();
        setBinNo(null);
        queryClient.invalidateQueries({
          queryKey: ['locations'],
        });
      } catch (error) {
        toast.error(utils.handleResponseError(error));
      }
    },
    [queryClient, t],
  );

  const updateLocation = useCallback(
    async (values: Partial<InventoryLocations>, id: number) => {
      try {
        await api.inventoryLocations.editInventoryLocation(values, id);
        toast.success(t('InventoryLocations.successUpdatingLocation'));
        modalRef.current.close();
        setBinNo(null);
        queryClient.invalidateQueries({
          queryKey: ['locations'],
        });
      } catch (error) {
        toast.error(utils.handleResponseError(error));
      }
    },
    [queryClient, t],
  );

  const onSubmit = useCallback(
    async (values: InventoryLocations, id?: number) => {
      values.storeNo = +values.storeNo;
      values.binNo = +values.binNo;
      values.maxShelfCapacity = +values.maxShelfCapacity;
      if (id && !isCopy) {
        updateLocation(values, id);
      } else {
        addLocations(values);
      }
    },
    [addLocations, isCopy, updateLocation],
  );

  const deleteLocation = useCallback(
    async (binNo: string) => {
      try {
        await api.inventoryLocations.deleteLocation(binNo);
        toast.success(t('successDeletingLocation'));
        onRefresh();
        queryClient.invalidateQueries({
          queryKey: ['locations'],
        });
      } catch (error) {
        toast.error(t('General.error'));
      }
    },
    [onRefresh, queryClient, t],
  );

  const handleDeletelocation = useCallback(
    async (binNo: number) => {
      const confirmDeletion = confirm({
        title: t('InventoryLocations.deleteLocationTitle'),
        confirmBtnText: t('Delete'),
        content: t('InventoryLocations.deleteLocation', {
          binNo,
        }),
      });

      if (await confirmDeletion) {
        deleteLocation(String(binNo));
      }
    },
    [t, deleteLocation],
  );

  const getTitle = useCallback(
    (binNo: number) => {
      if (isCopy) return t('InventoryLocations.copyLocation', { binNo });
      if (binNo) return t('InventoryLocations.edit');
      return t('InventoryLocations.add');
    },
    [isCopy, t],
  );

  return (
    <>
      <Table
        className="manage-users__table"
        fetching={isFetching}
        title={t('InventoryLocations.title', { skuNo: stateSkuNo || null })}
        labels={labels}
        elementProps={labels.map(({ key }) => key)}
        contentArray={renderContentArray(items)}
        buttonText={t('InventoryLocations.add')}
        onEdit={(el) => {
          setBinNo(el.binNo);
          modalRef.current.open();
        }}
        onDelete={(values) => handleDeletelocation(values.binNo)}
        onAddButtonClick={() => modalRef.current.open()}
        withActionButtons
        totalItems={totalItems}
        totalPages={totalPages}
        limit={limit}
        searchable
        searchBy={searchBy}
        searchByOptions={searchByOptions}
        searchPlaceholder={t('InventoryLocations.searchPlaceholder')}
        onSearchByChange={handleSearchByChange}
        onHeaderClick={handleSortClick}
        onCopy={(el) => {
          setBinNo(el.binNo);
          setIsCopy(true);
          modalRef.current.open();
        }}
      />
      <Modal
        focusableElements="div"
        className="manage-inventory-locations__modal"
        type="no-action"
        ref={modalRef}
        modalName="manage-inventory-locations"
        onClose={() => {
          setBinNo(null);
          setIsCopy(false);
        }}
        onX={() => {
          setBinNo(null);
          setIsCopy(false);
          modalRef.current.close();
        }}
        title={getTitle(binNo)}
      >
        <InventoryLocationsForm
          onSubmit={(values) => onSubmit(values, binNo)}
          handleCancel={() => {
            modalRef.current.close();
          }}
          binNo={binNo}
          storeNo={+storeNo}
          isCopy={isCopy}
        />
      </Modal>
    </>
  );
};

export default InventoryLocationsPage;
